import { useLazyInvoicesInvoicesRetrieveQuery } from '@bambi/api-client/bambi';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useCallback } from 'react';

import { editInvoice, setInvoiceModalOpen } from '../invoice.slice';
import { useAppDispatch } from 'src/app/store/hooks';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

// rows load independently, so only the most recent click may open its invoice
let latestRequest = 0;

export function useOpenInvoiceById(
  openModal: ActionCreatorWithPayload<boolean, string>
) {
  const dispatch = useAppDispatch();
  const [getInvoice] = useLazyInvoicesInvoicesRetrieveQuery();

  return useCallback(
    async (invoiceId: string) => {
      if (typeof invoiceId !== 'string') {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to fetch invoice',
            description: `Invoice ID is an unexpected type: ${typeof invoiceId}`,
          })
        );
        return;
      }

      const request = ++latestRequest;
      const { data, error } = await getInvoice({
        id: invoiceId,
      });

      if (request !== latestRequest) {
        return;
      }

      if (error) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to fetch invoice',
            description: formatServerError(error),
          })
        );
        return;
      }

      if (!data) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to fetch invoice',
            description: 'Invoice not found',
          })
        );
        return;
      }

      dispatch(editInvoice(data));
      dispatch(openModal(true));
    },
    [dispatch, getInvoice, openModal]
  );
}

export function useEditInvoiceById() {
  return useOpenInvoiceById(setInvoiceModalOpen);
}
